import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { documentoAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Layout } from '../components/Layout';
import { StatusBadge } from '../components/StatusBadge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea, ScrollBar } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
    FileText, 
    Search, 
    Filter, 
    Eye, 
    RefreshCw, 
    Download,
    FileSpreadsheet,
    Calendar,
    X,
    Loader2,
} from 'lucide-react';

export default function DocumentosPage() {
    const { user } = useAuth();
    const [documentos, setDocumentos] = useState([]);
    const [filteredDocs, setFilteredDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [estadoFilter, setEstadoFilter] = useState('todos');
    
    // Exportación
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportType, setExportType] = useState('total'); // 'total' | 'rango'
    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [exporting, setExporting] = useState(false);

    const canExport = ['admin', 'seguridad', 'superadmin'].includes(user?.rol);

    useEffect(() => {
        loadDocumentos();
    }, []);

    useEffect(() => {
        filterDocuments();
    }, [documentos, searchTerm, estadoFilter]);

    const loadDocumentos = async () => {
        setLoading(true);
        try {
            const response = await documentoAPI.getAllAdmin();
            setDocumentos(response.data);
        } catch (err) {
            console.error('Error loading documentos:', err);
            toast.error('Error al cargar las guías');
        } finally {
            setLoading(false);
        }
    };

    const filterDocuments = () => {
        let filtered = [...documentos];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (doc) =>
                    doc.folio?.toLowerCase().includes(term) ||
                    doc.folio_fisico?.toLowerCase().includes(term) ||
                    doc.patente?.toLowerCase().includes(term) ||
                    doc.patente_tracto?.toLowerCase().includes(term) ||
                    doc.rut_destino?.toLowerCase().includes(term) ||
                    doc.razon_social?.toLowerCase().includes(term) ||
                    doc.destino_info?.razon_social?.toLowerCase().includes(term) ||
                    doc.usuario_solicitante_nombre?.toLowerCase().includes(term) ||
                    doc.aprobacion?.aprobador_nombre?.toLowerCase().includes(term) ||
                    doc.conductor_nombre?.toLowerCase().includes(term) ||
                    doc.conductor_rut?.toLowerCase().includes(term)
            );
        }

        if (estadoFilter !== 'todos') {
            filtered = filtered.filter((doc) => doc.estado === estadoFilter);
        }

        setFilteredDocs(filtered);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            
            if (exportType === 'rango') {
                if (!fechaInicio || !fechaFin) {
                    toast.error('Debe seleccionar fecha inicio y fin');
                    setExporting(false);
                    return;
                }
                params.append('fecha_inicio', fechaInicio);
                params.append('fecha_fin', fechaFin);
            }
            
            if (estadoFilter !== 'todos') {
                params.append('estado', estadoFilter);
            }

            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/guias/exportar-excel?${params.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Error al exportar');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const fecha = new Date().toISOString().split('T')[0];
            a.download = `guias_despacho_${fecha}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast.success('Reporte exportado exitosamente');
            setShowExportDialog(false);
        } catch (err) {
            console.error('Error exporting:', err);
            toast.error('Error al exportar el reporte');
        } finally {
            setExporting(false);
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setEstadoFilter('todos');
    };

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Todas las Guías
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {filteredDocs.length} guía{filteredDocs.length !== 1 ? 's' : ''} encontrada{filteredDocs.length !== 1 ? 's' : ''}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {canExport && (
                            <Button
                                variant="outline"
                                onClick={() => setShowExportDialog(true)}
                                className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                                data-testid="export-btn"
                            >
                                <FileSpreadsheet className="h-4 w-4 mr-2" />
                                Exportar Excel
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={loadDocumentos}
                            className="border-2"
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Actualizar
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="flex flex-col gap-4">
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Buscar por folio, N° guía/factura, patente, RUT, empresa, solicitante, autorizador, conductor..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 input-industrial"
                                        data-testid="search-input"
                                    />
                                </div>
                                <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                                    <SelectTrigger className="w-full md:w-48 input-industrial" data-testid="estado-filter">
                                        <Filter className="h-4 w-4 mr-2" />
                                        <SelectValue placeholder="Estado" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="todos">Todos los estados</SelectItem>
                                        <SelectItem value="pendiente">Pendiente</SelectItem>
                                        <SelectItem value="autorizado">Autorizado</SelectItem>
                                        <SelectItem value="despachado">Despachado</SelectItem>
                                        <SelectItem value="rechazado">Rechazado</SelectItem>
                                    </SelectContent>
                                </Select>
                                {(searchTerm || estadoFilter !== 'todos') && (
                                    <Button
                                        variant="ghost"
                                        onClick={clearFilters}
                                        className="text-muted-foreground"
                                    >
                                        <X className="h-4 w-4 mr-1" />
                                        Limpiar
                                    </Button>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Table */}
                <Card className="border-2">
                    <ScrollArea className="w-full">
                        <Table className="table-industrial">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Folio</TableHead>
                                    <TableHead>N° Guía/Factura</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Patente</TableHead>
                                    <TableHead>Empresa Destino</TableHead>
                                    <TableHead>Conductor</TableHead>
                                    <TableHead>Solicitante</TableHead>
                                    <TableHead>Autorizador</TableHead>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <>
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <TableRow key={i}>
                                                <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                                <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                                <TableCell><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                                            </TableRow>
                                        ))}
                                    </>
                                ) : filteredDocs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={11}>
                                            <div className="empty-state py-12">
                                                <FileText className="empty-state-icon" />
                                                <p className="empty-state-title">Sin guías</p>
                                                <p className="empty-state-description">
                                                    {searchTerm || estadoFilter !== 'todos'
                                                        ? 'No se encontraron guías con los filtros aplicados'
                                                        : 'No hay guías registradas en el sistema'}
                                                </p>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredDocs.map((doc) => (
                                        <TableRow key={doc.id} data-testid={`row-${doc.folio}`}>
                                            <TableCell>
                                                <span className="folio-display">{doc.folio}</span>
                                            </TableCell>
                                            <TableCell>
                                                <span className="font-mono font-semibold" data-testid={`folio-fisico-${doc.folio}`}>
                                                    {doc.folio_fisico || '—'}
                                                </span>
                                            </TableCell>
                                            <TableCell className="capitalize">{doc.tipo}</TableCell>
                                            <TableCell className="font-mono">
                                                {doc.patente_tracto || doc.patente}
                                                {doc.patente_semiremolque && (
                                                    <span className="text-muted-foreground text-xs block">
                                                        Semi: {doc.patente_semiremolque}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div>
                                                    <span className="font-medium">
                                                        {doc.razon_social || doc.destino_info?.razon_social || '-'}
                                                    </span>
                                                    {doc.rut_destino && (
                                                        <span className="text-muted-foreground text-xs block">
                                                            {doc.rut_destino}
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {doc.conductor_nombre || '-'}
                                                {doc.conductor_rut && (
                                                    <span className="text-muted-foreground text-xs block">
                                                        {doc.conductor_rut}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell>{doc.usuario_solicitante_nombre}</TableCell>
                                            <TableCell>
                                                {doc.aprobacion?.aprobador_nombre || '-'}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-sm">
                                                {formatDate(doc.created_at)}
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge estado={doc.estado} size="sm" />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Link to={`/guias/documento/${doc.id}`}>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        data-testid={`view-${doc.folio}`}
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                        <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                </Card>
            </div>

            {/* Export Dialog */}
            <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                            Exportar a Excel
                        </DialogTitle>
                        <DialogDescription>
                            Descargue un reporte de guías de despacho en formato Excel
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Tipo de exportación</Label>
                            <Select value={exportType} onValueChange={setExportType}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="total">Todas las guías</SelectItem>
                                    <SelectItem value="rango">Por rango de fechas</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {exportType === 'rango' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Fecha inicio</Label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            type="date"
                                            value={fechaInicio}
                                            onChange={(e) => setFechaInicio(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Fecha fin</Label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            type="date"
                                            value={fechaFin}
                                            onChange={(e) => setFechaFin(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {estadoFilter !== 'todos' && (
                            <div className="p-3 bg-muted rounded-lg text-sm">
                                <span className="text-muted-foreground">Filtro aplicado:</span>{' '}
                                <span className="font-medium capitalize">{estadoFilter}</span>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowExportDialog(false)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleExport}
                            disabled={exporting}
                            className="bg-emerald-600 hover:bg-emerald-700"
                        >
                            {exporting ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Exportando...
                                </>
                            ) : (
                                <>
                                    <Download className="h-4 w-4 mr-2" />
                                    Descargar Excel
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
